import React, { useState, useEffect } from 'react'
import { Grid, Row } from 'react-bootstrap'

import PropTypes from 'prop-types'

import {
  SubHeaderNavigationFlexWrapAuto,
  SubHeaderNavigationInlineBlock
} from '../../components/SubHeader'

import { onSvgLoad } from '../../helpers/utils'
import ProductNameHOC from '../../hocs/ProductNameHOC'
import { getPalettePath } from '../../styles/constants'
import { useAppContext } from '../App/Context'

import { StyledSubHeaderContainerWrapper } from './styled/StyledSubHeaderContainerWrapper'

/**
 * Controls stateful logic to contain the returned type of `SubHeader`
 * based on application-wide settings or provided props.
 *
 * Bound to overridiable styles in `StyledSubHeaderContainerWrapper` along
 * with the `components/SubHeader/styled/*` styled `typeOf SubHeader` returned.
 *
 * @see StyledSubHeaderContainerWrapper
 * @see {@link ../../components/SubHeader SubHeader Module}
 *
 * @param {{
*   toggleSearchModal: () => void,
*   devices: Array<Device>,
*   language: string,
*   currentDevice: Device,
*   clearDevice: () => void,
*   toggleDeviceModal: () => void,
*   query: object,
*   modulesAvailable: Array<ModulesAvailable>,
*   externalNavEnabled?: boolean,
*   getOption: (key: string) => string,
*   productNameSingular: string,
*   productTranslation: string
* }}
* @return {React.ReactNode}
*/
const SubHeaderContainer = ({
  toggleSearchModal,
  devices,
  language,
  currentDevice,
  clearDevice,
  toggleDeviceModal,
  query,
  modulesAvailable,
  externalNavEnabled,
  getOption,
  productNameSingular,
  productTranslation,
}) => {
  const { versionKey: version } = useAppContext()
  const palette = getPalettePath(version)

  const [showMoreDropdown, setShowMoreDropdown] = useState(false)

  useEffect(() => {
    if (!currentDevice) {
      setShowMoreDropdown(false)
    }
  }, [currentDevice])

  const headerLogoLink = getOption('header.logoLink')

  /**
   * A predicate to determine the type of SubHeader to render.
   * @todo This is equivalent to `!( isVersion3 || isVersion2 )` which are available in context.
   * Consider consuming the same memoized value pre-calculated from provider.
   */
  const isSubNavigationFlexWrapPreferred = version !== 'v1'

  return (
    <StyledSubHeaderContainerWrapper id="sub-header-wrapper" palette={palette}>
      <Grid>
        <Row>
          {isSubNavigationFlexWrapPreferred ? (
            <SubHeaderNavigationInlineBlock
              currentDevice={currentDevice}
              headerLogoLink={headerLogoLink}
              query={query}
              language={language}
              onSvgLoad={onSvgLoad}
              modulesAvailable={modulesAvailable}
              toggleMoreDropdown={() => setShowMoreDropdown(!showMoreDropdown)}
              showMoreDropdown={showMoreDropdown}
              devices={devices}
              toggleDeviceModal={toggleDeviceModal}
              clearDevice={clearDevice}
              productNameSingular={productNameSingular}
              productTranslation={productTranslation}
              toggleSearchModal={toggleSearchModal}
              externalNavEnabled={externalNavEnabled}

            />
          ) : (
            <SubHeaderNavigationFlexWrapAuto
              currentDevice={currentDevice}
              headerLogoLink={headerLogoLink}
              query={query}
              language={language}
              onSvgLoad={onSvgLoad}
              modulesAvailable={modulesAvailable}
              devices={devices}
              toggleDeviceModal={toggleDeviceModal}
              clearDevice={clearDevice}
              productNameSingular={productNameSingular}
              productTranslation={productTranslation}
              toggleSearchModal={toggleSearchModal}
              externalNavEnabled={externalNavEnabled}

            />
          )}
        </Row>
      </Grid>
    </StyledSubHeaderContainerWrapper>
  )
}

export const devicePropInterface = PropTypes.shape({
  customerID: PropTypes.string,
  id: PropTypes.number.isRequired,
  image_location: PropTypes.string.isRequired,
  manufacturer: PropTypes.string,
  name: PropTypes.string,
  name_pretty: PropTypes.string.isRequired,
  os: PropTypes.string,
  slug: PropTypes.string.isRequired,
  vanity_name: PropTypes.string
})

SubHeaderContainer.propTypes = {
  toggleSearchModal: PropTypes.func.isRequired,
  devices: PropTypes.arrayOf(devicePropInterface).isRequired,
  language: PropTypes.string.isRequired,
  currentDevice: devicePropInterface,
  clearDevice: PropTypes.func.isRequired,
  toggleDeviceModal: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  customer: PropTypes.string.isRequired,
  modulesAvailable: PropTypes.shape({
    tutorials: PropTypes.bool,
    faqs: PropTypes.bool,
    guides: PropTypes.bool,
    videos: PropTypes.bool
  }).isRequired,
  externalNavEnabled: PropTypes.bool,
  getOption: PropTypes.func.isRequired
}

export default ProductNameHOC(SubHeaderContainer)



// WEBPACK FOOTER //
// ./src/containers/SubHeader/SubHeader.js